import { Clipboard } from '@angular/cdk/clipboard';
import { CurrencyPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { GetTransactionsType } from 'src/app/shared/enums/get-transactions-type';
import { ParameterTypeEnum } from 'src/app/shared/enums/parameter-type.enum';
import { UtcToLocalTimePipe } from 'src/app/shared/pipes/utc-to-local-time.pipe';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { CuentaTrabajo } from '../../interfaces/cuenta-trabajo.interface';
import { Transaccion } from '../../interfaces/transaccion.interface';
import { BalanceCuentaTrabajoService } from '../../services/balance-cuenta-trabajo.service';
import { ParameterService } from '../../services/parameter.service';
import { TransaccionService } from '../../services/transaccion.service';
import { WorkingAccountService } from '../../services/working-account.service';
import { AssignTransactionComponent } from '../admin-transactions/assign-transaction/assign-transaction.component';
import { EditTransactionModalComponent } from '../assigned-transactions/edit-transaction-modal/edit-transaction-modal.component';
import { RejectTransactionModalComponent } from '../assigned-transactions/reject-transaction-modal/reject-transaction-modal.component';
import { SendTransactionModalComponent } from '../assigned-transactions/send-transaction-modal/send-transaction-modal.component';
import { getColumns } from '../assigned-transactions/table-transactions-columns';

@Component({
  selector: 'app-detalle-cajero',
  templateUrl: './detalle-cajero.component.html',
  styleUrls: ['./detalle-cajero.component.sass'],
})
export class DetalleCajeroComponent implements OnInit, OnDestroy {
  constructor(
    private datePipe: UtcToLocalTimePipe,
    private workingAccountService: WorkingAccountService,
    private spinner: SpinnerService,
    private currencyPipe: CurrencyPipe,
    private route: ActivatedRoute,
    private router: Router,
    private transactionService: TransaccionService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private auth: CognitoService,
    private balanceCuentaTrabajoService: BalanceCuentaTrabajoService,
    private parameterService: ParameterService,
    @Optional() public dialogRef: MatDialogRef<DetalleCajeroComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: {
      cuenta_trabajo_id: number;
      transacciones: Transaccion[];
      statusCajero: any[];
      countryCode: string[];
    },
  ) {}

  statusCajero: any[] = [];
  countryCodes = ['VE', 'US', 'CL'];
  dataSource: MatTableDataSource<Transaccion> | undefined;
  transacciones: Transaccion[] = [];
  transaccionesFiltradas: Transaccion[] = [];
  expandedCardId: number | null = null;

  cuentasTrabajo: CuentaTrabajo[] = [];

  cuentaSeleccionada: number | undefined;
  cuentaTrabajo: CuentaTrabajo | undefined;
  saldoSistema: number = 0;
  saldoBanco: number = 0;
  diferenciaSaldo: number = 0;
  counter: number = 0;
  balanceIngresado: number = 0;

  get saldosNoCoinciden(): boolean {
    return Math.abs(this.saldoSistema - this.saldoBanco) > 5;
  }

  private balanceRefreshInterval: any;

  /**
   * Cuentas de trabajo dedicadas a los rails automáticos, leídas de los
   * parámetros CUENTA_TRABAJO_N58 / CUENTA_TRABAJO_BANCO_PLAZA (el `valor`
   * es el `cuenta_trabajo_id`). Una tx asignada a una de estas cuentas y aún
   * POR_PROCESAR está siendo pagada (o en verificación) por ese rail → se
   * bloquea en la vista para que ningún operador la tome en paralelo. Es un
   * candado SÓLO visual; la protección dura vive en el backend.
   */
  private railWorkingAccounts = new Map<number, 'N58' | 'Banco Plaza'>();

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date(), [Validators.required]),
    end: new FormControl<Date | null>(new Date(), [Validators.required]),
  });

  columns = getColumns(this.currencyPipe);
  displayedColumns: string[] = [
    'seleccion',
    'id',
    'userId',
    'beneficiario',
    'destino',
    'totalMonedaDestino',
    'pago',
    'acciones',
  ];
  emailOperador = '';
  cambios: any = {};

  async ngOnInit() {
    let authData = await this.auth.getUser();
    this.emailOperador = authData.attributes.email;

    await this.loadRailWorkingAccounts();

    if (
      this.data?.cuenta_trabajo_id &&
      this.data?.transacciones &&
      this.data?.statusCajero
    ) {
      this.transacciones = this.data.transacciones;
      this.statusCajero = this.data.statusCajero;
      this.cuentasTrabajo = this.statusCajero.map((cajero) => cajero.cuenta);

      this.cuentaSeleccionada = this.data.cuenta_trabajo_id;
      this.cuentaTrabajo = this.cuentasTrabajo.find(
        (res) => res.cuenta_trabajo_id === this.cuentaSeleccionada,
      );

      await this.loadBalanceCuentaTrabajo();
      this.updateSaldos();
      this.filterTransactions();
      this.startBalanceAutoRefresh();
    } else {
      this.getWorkingAccounts().then(() => {
        this.route.queryParams.subscribe(async (params) => {
          if (params['cuenta_trabajo_id']) {
            const cuentaId = +params['cuenta_trabajo_id'];
            this.cuentaSeleccionada = cuentaId;
            this.cuentaTrabajo = this.cuentasTrabajo.find(
              (res) => res.cuenta_trabajo_id === this.cuentaSeleccionada,
            );
            await this.loadBalanceCuentaTrabajo();
            await this.loadDataWithFallback();
            this.startBalanceAutoRefresh();
          }
        });
      });
    }
  }

  ngOnDestroy() {
    if (this.balanceRefreshInterval) {
      clearInterval(this.balanceRefreshInterval);
    }
  }

  startBalanceAutoRefresh() {
    this.balanceRefreshInterval = setInterval(async () => {
      await this.loadBalanceCuentaTrabajo();
      this.updateSaldos();
    }, 120000);
  }

  async loadBalanceCuentaTrabajo() {
    if (!this.cuentaSeleccionada) return;

    try {
      const balanceData: any =
        await this.balanceCuentaTrabajoService.getBalanceCuentaTrabajo(
          this.cuentaSeleccionada,
        );
      this.saldoBanco = balanceData.data.balance || 0;
      this.counter = balanceData.data.counter || 0;
      this.diferenciaSaldo =
        Math.abs(this.saldoSistema) - Math.abs(this.saldoBanco);
    } catch (error) {
      this.saldoBanco = 0;
      this.counter = 0;
      this.diferenciaSaldo =
        Math.abs(this.saldoSistema) - Math.abs(this.saldoBanco);
    }
  }

  /**
   * Lee CUENTA_TRABAJO_N58 y CUENTA_TRABAJO_BANCO_PLAZA y arma el mapa
   * `railWorkingAccounts` (cuenta_trabajo_id → rail). Si la lectura falla el
   * mapa queda vacío → no se bloquea nada (comportamiento histórico), nunca
   * al revés: preferimos no bloquear de más ante un error de parámetros.
   */
  private async loadRailWorkingAccounts(): Promise<void> {
    try {
      const [n58Param, bpParam] = await Promise.all([
        this.parameterService.getParameter(ParameterTypeEnum.CUENTA_TRABAJO_N58),
        this.parameterService.getParameter(
          ParameterTypeEnum.CUENTA_TRABAJO_BANCO_PLAZA,
        ),
      ]);
      const map = new Map<number, 'N58' | 'Banco Plaza'>();
      const n58Id = Number(n58Param?.valor);
      const bpId = Number(bpParam?.valor);
      if (Number.isFinite(n58Id) && n58Id > 0) map.set(n58Id, 'N58');
      if (Number.isFinite(bpId) && bpId > 0) map.set(bpId, 'Banco Plaza');
      this.railWorkingAccounts = map;
    } catch {
      this.railWorkingAccounts = new Map();
    }
  }

  /**
   * Rail automático que está procesando la fila, o null. Una tx cuya cuenta
   * de trabajo es la de N58 o Banco Plaza sigue POR_PROCESAR mientras el rail
   * paga (o queda en verificación), pero NO debe poder tomarla un operador.
   */
  procesandoRail(row: any): 'N58' | 'Banco Plaza' | null {
    const id = Number(
      row?.cuentaTrabajoID ?? row?.cuentaTrabajo?.cuenta_trabajo_id,
    );
    if (!Number.isFinite(id) || id <= 0) return null;
    return this.railWorkingAccounts.get(id) ?? null;
  }

  /** Helper booleano para el template. */
  isProcesandoRail(row: any): boolean {
    return this.procesandoRail(row) !== null;
  }

  filterTransactions() {
    if (!this.cuentaSeleccionada) {
      this.transaccionesFiltradas = [];
      this.dataSource = new MatTableDataSource<Transaccion>([]);
      return;
    }

    this.transaccionesFiltradas = this.transacciones.filter(
      (transaccion) =>
        transaccion.cuentaTrabajo?.cuenta_trabajo_id ===
        this.cuentaSeleccionada,
    );

    this.dataSource = new MatTableDataSource<Transaccion>(
      this.transaccionesFiltradas,
    );
  }

  async onCuentaTrabajoChange() {
    this.cuentaTrabajo = this.cuentasTrabajo.find(
      (res) => res.cuenta_trabajo_id === this.cuentaSeleccionada,
    );
    await this.loadBalanceCuentaTrabajo();
    this.updateSaldos();
    this.filterTransactions();
  }

  updateSaldos() {
    const cajeroInfo = this.statusCajero.find(
      (cajero) => cajero.cuenta.cuenta_trabajo_id === this.cuentaSeleccionada,
    );

    if (cajeroInfo) {
      this.saldoSistema = cajeroInfo.total || 0;
      this.diferenciaSaldo =
        Math.abs(this.saldoSistema) - Math.abs(this.saldoBanco);
    } else {
      this.saldoSistema = 0;
      this.diferenciaSaldo =
        Math.abs(this.saldoSistema) - Math.abs(this.saldoBanco);
    }
  }

  validarSaldoAhora() {}

  async enviarBalanceBanco() {
    if (!this.cuentaSeleccionada || !this.balanceIngresado) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Por favor ingresa el saldo del banco',
      });
      return;
    }

    try {
      Swal.fire({
        title: 'Guardando balance...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await this.balanceCuentaTrabajoService.createBalanceCuentaTrabajo({
        cuentaTrabajoId: this.cuentaSeleccionada,
        balance: Number(this.balanceIngresado),
      });

      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Balance actualizado correctamente',
        timer: 2000,
      });

      this.balanceIngresado = 0;
      this.counter = 0;
      await this.loadBalanceCuentaTrabajo();
      await this.refreshData();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el balance',
      });
    }
  }

  async refreshData() {
    if (this.data?.transacciones) {
      const ref = this.spinner.start();
      await this.loadRailWorkingAccounts();
      this.statusCajero = await this.workingAccountService.getStatusCajero(
        this.data?.countryCode ?? this.countryCodes,
      );
      const transaccionesResult = await this.transactionService.getAllTransactions(
        GetTransactionsType.ASSIGNED,
        undefined,
      );
      this.transacciones = Array.isArray(transaccionesResult) ? transaccionesResult : (transaccionesResult as any)?.query ?? [];
      this.filterTransactions();
      this.updateSaldos();
      await this.loadBalanceCuentaTrabajo();

      this.spinner.stop(ref);
    } else {
      await this.loadData();
    }
  }

  async loadDataWithFallback() {
    if (!this.cuentaSeleccionada || !this.range.valid) {
      return;
    }

    const ref = this.spinner.start();

    this.spinner.stop(ref);
  }

  getTipoTransaccion(banco: string, tipoTransaccion: string) {
    if (tipoTransaccion === 'DEBITO') {
      if (banco === this.cuentaTrabajo?.banco?.nombre) {
        return 'TMB';
      }
      return 'TOB';
    }
    return '';
  }

  async loadData() {
    if (this.cuentaSeleccionada && this.range.valid) {
      this.cuentaTrabajo = this.cuentasTrabajo.find(
        (res) => res.cuenta_trabajo_id === this.cuentaSeleccionada,
      );
      const ref = this.spinner.start();

      // Ensure start and end are the same date
      const startDate = this.range.controls['start'].value || new Date();
      this.range.controls['end'].setValue(startDate);

      this.statusCajero = await this.workingAccountService.getBalancesCajero(
        this.cuentaSeleccionada,
        startDate,
        startDate,
      );
      this.dataSource = new MatTableDataSource(this.statusCajero);
      this.spinner.stop(ref);
    }
  }

  async getWorkingAccounts() {
    this.cuentasTrabajo = await this.workingAccountService.getWorkingAccounts(
      this.countryCodes,
    );
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  goBack() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.router.navigate(['/wallet/admin/status-cajero']);
    }
  }

  isObjectEmpty = (objectName: any) => {
    return Object.keys(objectName).length === 0;
  };

  copy(element: Transaccion) {
    const beneficiario = element.cuentaBeneficiario;
    const nombreCompleto = `${beneficiario?.nombre || ''} ${beneficiario?.apellidos || ''}`.trim();
    const banco = beneficiario?.banco?.nombre || 'Sin banco';
    const tipoCuenta = beneficiario?.tipoCuenta?.nombre?.toUpperCase() || '';
    const numeroCuenta = beneficiario?.numeroCuenta || '';
    const numeroID = beneficiario?.numeroID || '';
    const monto = this.formatCurrencyForCopy((element as any).totalMonedaDestino);

    const text =
      `ID: ${element.id}\n` +
      `ID Cliente: ${(element as any).userId ?? ''}\n` +
      `Beneficiario: ${nombreCompleto}\n` +
      `DNI: ${numeroID}\n` +
      `Banco: ${banco}\n` +
      `Tipo cuenta: ${tipoCuenta}\n` +
      `N° cuenta: ${numeroCuenta}\n` +
      `Monto: ${monto}`;

    this.clipboard.copy(text);
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });

    Toast.fire({
      icon: 'success',
      title: 'Transferencia copiada al portapapeles',
    });
  }

  async setPurchaseValue(transactionId: number, id: number) {
    if (!transactionId || !id) return;

    const value = (<HTMLInputElement>(
      document.getElementById(`valorCompra${id}`)
    )).value.replaceAll(',', '');
    Swal.fire({
      title: 'Guardando precio de compra ',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    await this.transactionService.setPurchaseValue({
      transactionId,
      value,
    });
    Swal.close();
  }

  async guardarSeleccion() {
    const cambioFormateados = Object.keys(this.cambios).map((key) => {
      return {
        id: +key,
        checked: this.cambios[key],
      };
    });
    const body = {
      idTransaction: cambioFormateados,
      operatorEmail: this.emailOperador,
    };
    await this.transactionService.changeAssignationStatus(body);
    await this.refreshData();
  }

  change(event: any, id: number) {
    this.cambios[id] = event.checked;
  }

  checkEmailOperador(email: string) {
    if (!email) return false;
    if (this.emailOperador !== email) return true;
    return false;
  }

  disableButton(email: string) {
    if (this.emailOperador !== email) return true;
    return false;
  }

  formatCurrencyForCopy(value: number): string {
    const valueString = value.toString();
    return valueString.replace(/\./g, ',');
  }

  openSendTransactionModal(row: any) {
    const dialogRef = this.dialog.open(SendTransactionModalComponent, {
      data: row,
      maxHeight: '70%',
      maxWidth: '826px',
      minWidth: '800px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        if (this.cuentaSeleccionada) {
          await this.balanceCuentaTrabajoService.updateCounterBalanceCuentaTrabajo(
            {
              cuentaTrabajoId: this.cuentaSeleccionada,
            },
          );
          await this.loadBalanceCuentaTrabajo();
        }
        await this.refreshData();
      }
    });
  }

  openEditTransactionModal(row: any) {
    const dialogRef = this.dialog.open(EditTransactionModalComponent, {
      data: row,
      maxHeight: '70%',
      maxWidth: '826px',
      minWidth: '800px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        await this.refreshData();
      }
    });
  }

  openRejectTransactionModal(row: any) {
    const dialogRef = this.dialog.open(RejectTransactionModalComponent, {
      data: row,
      maxHeight: '70%',
      maxWidth: '826px',
      minWidth: '800px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        await this.refreshData();
      }
    });
  }

  async reasign(row: any) {
    row.isEditing = false;
    const dialogRef = this.dialog.open(AssignTransactionComponent, {
      data: {
        trxId: row.id,
        type: 'reassign',
      },
      maxHeight: '70%',
      maxWidth: '826px',
      minWidth: '800px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        await this.refreshData();
      }
    });
  }

  doFilter = (event: any) => {
    const element = event.currentTarget as HTMLInputElement;
    const value = element.value;
    if (this.dataSource)
      this.dataSource.filter = value.trim().toLocaleLowerCase();
  };
}
